import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "page-wrapper"
}
const _hoisted_2 = {
  key: 1,
  class: "page-wrapper",
  id: "assessment-detail-main-content",
  tabindex: "-1"
}
const _hoisted_3 = { class: "breadcrumb-wrapper" }
const _hoisted_4 = { class: "content-frame-wrapper" }
const _hoisted_5 = { class: "header-wrapper" }
const _hoisted_6 = {
  role: "heading",
  "aria-level": "1",
  class: "page-title",
  "data-test": "title"
}
const _hoisted_7 = { class: "badge-wrapper" }
const _hoisted_8 = {
  key: 0,
  class: "results-wrapper"
}
const _hoisted_9 = {
  key: 1,
  class: "assessment-recommendations-wrapper",
  "data-test": "assessment-recommendations-skeleton"
}
const _hoisted_10 = {
  key: 2,
  class: "assessment-recommendations-outer-wrapper"
}
const _hoisted_11 = {
  class: "assessment-recommendations-wrapper",
  "data-test": "assessment-recommendations"
}
const _hoisted_12 = { class: "assessment-detail-wrapper" }

import { ref, computed } from "vue";
import { IonPage, IonContent, IonBadge, IonIcon } from "@ionic/vue";
import MobileHeader from "@/components/Common/MobileHeader/MobileHeader.vue";
import { checkmarkCircleOutline } from "ionicons/icons";
import { useAssessmentStore, useErrorStore, useLocaleStore } from "@/store";
import PageBanner from "@/components/Common/PageBanner/PageBanner.vue";
import Breadcrumbs from "@/components/Common/Breadcrumbs/Breadcrumbs.vue";
import AssessmentDetailSkeleton from "@/components/Skeletons/AssessmentDetailSkeleton/AssessmentDetailSkeleton.vue";
import DesktopLocaleSelector from "@/components/Common/LocaleSelector/DesktopLocaleSelector.vue";
import { createLocaleSpecificSanityId } from "@/helpers/CreateLocaleSpecificSanityId.js";
import AssessmentDetailContent from "@/components/Assessment/AssessmentDetailContent/AssessmentDetailContent.vue";
import AssessmentScores from "@/components/Assessment/AssessmentScores/AssessmentScores.vue";
import AssessmentRecommendations from "@/components/Assessment/AssessmentRecommendations/AssessmentRecommendations.vue";
import AssessmentBadges from "@/components/Assessment/AssessmentBadges/AssessmentBadges.vue";
import LastCompleted from "@/components/Assessment/LastCompleted/LastCompleted.vue";
import { onIonViewDidEnter } from "@ionic/vue";
import { parseImageUrl } from "@/helpers";
import isEmpty from "lodash/isEmpty";
import AssessmentRecommendationsSkeleton from "@/components/Skeletons/AssessmentRecommendationsSkeleton/AssessmentRecommendationsSkeleton.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'AssessmentDetail',
  props: {
  id: { type: String, required: true },
},
  setup(__props) {

const assessmentStore = useAssessmentStore();
const localeStore = useLocaleStore();
const assessment = computed(() => assessmentStore.getAssessmentDetail ?? null);
const props = __props;

const isAssessmentLoaded = computed(
  () =>
    assessmentStore.isAssessmentDetailLoaded &&
    assessment?.value.id &&
    assessment?.value.baseId === props.id &&
    assessmentStore.isAssessmentResultsLoaded
);

const assessmentCompletedDate = computed(() => {
  return assessmentStore.getAssessmentResults?.data?.completedDate;
});

const bannerImage = ref(require("@/assets/course-thumbnail-placeholder.png"));

const assessmentScoresExist = computed(
  () => !isEmpty(assessmentStore.getAssessmentResults?.data?.totalScore)
);

const incompleteAssessmentExist = computed(
  () => assessmentStore.getIncompleteAssessment !== null
);

const isAssessmentRecommendationsLoaded = computed(() => {
  return assessmentStore.isAssessmentRecommendationsLoaded;
});

const assessmentRecommendationsExist = computed(
  () => !isEmpty(assessmentStore.getAssessmentRecommendations)
);

const updateBannerImage = (): void => {
  if (assessment.value.featureImage) {
    bannerImage.value = parseImageUrl(
      assessment?.value.featureImage,
      1000,
      2500
    );
  }
};

const loadAssessment = async (id: string): Promise<void> => {
  await assessmentStore.requestAssessmentDetail(id);
  updateBannerImage();
  const baseSanityId = assessmentStore.getAssessmentDetail.baseId;
  await assessmentStore.requestAssessmentResults(baseSanityId);
};

onIonViewDidEnter(async (): Promise<void> => {
  if (assessment?.value.id && assessment?.value.baseId === props.id) {
    // Assessment is already loaded
    return;
  }
  //load the resource using the sanity ID with the correct locale suffix added.
  const sanityId = createLocaleSpecificSanityId(props.id);
  loadAssessment(sanityId).catch((error) => {
    if (
      //if the resource specific to the locale selected cannot be accessed, and the locale selected is an english locale, load the global english version.
      error.response.status === 403 &&
      localeStore.getCurrentLocaleCode.includes("en")
    ) {
      loadAssessment(props.id as string);
    } else {
      //otherwise, if the locale selected is not an english locale and the non english locale version cannot be accessed, load an error state.
      assessmentStore.$reset();
      const errorStore = useErrorStore();
      errorStore.setGenericError(true);
    }
  });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonPage), null, {
    default: _withCtx(() => [
      _createVNode(MobileHeader),
      _createVNode(_unref(IonContent), {
        "data-test": "learn-detail",
        class: "page-wrapper"
      }, {
        default: _withCtx(() => [
          _createVNode(DesktopLocaleSelector),
          _createVNode(PageBanner, {
            isImageLoading: !isAssessmentLoaded.value,
            imagePath: bannerImage.value
          }, null, 8, ["isImageLoading", "imagePath"]),
          (!isAssessmentLoaded.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(AssessmentDetailSkeleton)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  (assessment.value)
                    ? (_openBlock(), _createBlock(Breadcrumbs, {
                        key: 0,
                        tab: "tools",
                        doc: assessment.value.title,
                        class: "breadcrumbs"
                      }, null, 8, ["doc"]))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    (assessmentScoresExist.value)
                      ? (_openBlock(), _createBlock(_unref(IonBadge), {
                          key: 0,
                          id: "completion-badge"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(IonIcon), {
                              icon: _unref(checkmarkCircleOutline),
                              "aria-hidden": "true"
                            }, null, 8, ["icon"]),
                            _createTextVNode(" " + _toDisplayString(_ctx.$t("assessment.quizCompleted")), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    _createElementVNode("h1", _hoisted_6, _toDisplayString(assessment.value.title), 1),
                    (assessmentCompletedDate.value)
                      ? (_openBlock(), _createBlock(LastCompleted, {
                          key: 1,
                          "last-completed": assessmentCompletedDate.value,
                          text: _ctx.$t('assessment.lastCompleted')
                        }, null, 8, ["last-completed", "text"]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(AssessmentBadges, {
                      label: assessment.value.badge,
                      time: assessment.value.time,
                      timeUnit: _ctx.$t('assessment.mins')
                    }, null, 8, ["label", "time", "timeUnit"])
                  ]),
                  (assessmentScoresExist.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createVNode(AssessmentScores)
                      ]))
                    : _createCommentVNode("", true),
                  (!isAssessmentRecommendationsLoaded.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _createVNode(AssessmentRecommendationsSkeleton)
                      ]))
                    : _createCommentVNode("", true),
                  (assessmentRecommendationsExist.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createElementVNode("div", _hoisted_11, [
                          _createVNode(AssessmentRecommendations)
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_12, [
                    _createVNode(AssessmentDetailContent, {
                      assessment: assessment.value,
                      assessmentScoresExist: assessmentScoresExist.value,
                      incompleteAssessmentExist: incompleteAssessmentExist.value
                    }, null, 8, ["assessment", "assessmentScoresExist", "incompleteAssessmentExist"])
                  ])
                ])
              ]))
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})